<template>
	<transition name="upload-tray-fade">
		<div
			v-if="uploadsStore.trayOpen && uploadsStore.totalCount > 0"
			class="upload-tray"
			data-test="upload-tray"
		>
			<div class="upload-tray__header">
				<CloudUpload16 class="upload-tray__header-icon" />
				<span class="sgnl-paragraph-s-semibold upload-tray__title sgnl-text-color-default">{{
					headerText
				}}</span>
				<v-spacer />
				<v-btn
					icon
					variant="text"
					size="x-small"
					:aria-label="collapsed ? 'Expand uploads' : 'Collapse uploads'"
					@click="collapsed = !collapsed"
				>
					<ChevronUp16 v-if="collapsed" />
					<ChevronDown16 v-else />
				</v-btn>
				<v-btn
					icon
					variant="text"
					size="x-small"
					:disabled="uploadsStore.hasActive"
					aria-label="Dismiss uploads"
					@click="uploadsStore.clearFinished()"
				>
					<Close16 />
				</v-btn>
			</div>
			<v-expand-transition>
				<div
					v-show="!collapsed"
					class="upload-tray__body"
				>
					<div
						v-for="item in uploadsStore.items"
						:key="item.id"
						class="upload-tray__row"
					>
						<div class="upload-tray__status">
							<v-progress-circular
								v-if="item.status === 'uploading'"
								indeterminate
								:width="2"
								:size="16"
								class="sgnl-icon-color-brand"
							/>
							<Checkmark16
								v-else-if="item.status === 'success'"
								class="sgnl-icon-color-success"
							/>
							<ErrorFilled16
								v-else-if="item.status === 'failed'"
								class="sgnl-icon-color-error"
							/>
							<span
								v-else
								class="upload-tray__queued-dot"
							/>
						</div>
						<div class="upload-tray__details">
							<div
								class="sgnl-paragraph-s-regular upload-tray__name sgnl-text-color-default"
								:title="item.fileName"
							>
								{{ item.fileName }}
							</div>
							<div
								v-if="item.status === 'failed' && item.error"
								class="sgnl-footnote-regular upload-tray__error sgnl-text-color-error"
							>
								{{ item.error }}
							</div>
						</div>
						<v-btn
							v-if="uploadsStore.retryableItems.includes(item)"
							variant="text"
							size="x-small"
							class="upload-tray__retry sgnl-text-color-brand"
							@click="uploadsStore.retry(item.id)"
						>
							<Renew16 />
							<span>Retry</span>
						</v-btn>
						<v-btn
							v-if="item.status === 'queued' || item.status === 'failed'"
							icon
							variant="text"
							size="x-small"
							class="upload-tray__remove"
							:aria-label="item.status === 'queued' ? 'Cancel upload' : 'Remove upload'"
							@click="uploadsStore.removeItem(item.id)"
						>
							<Close16 />
						</v-btn>
					</div>
				</div>
			</v-expand-transition>
			<div
				v-if="uploadsStore.retryableItems.length > 1"
				class="upload-tray__footer"
			>
				<v-btn
					variant="text"
					size="small"
					class="upload-tray__retry-all sgnl-text-color-brand"
					@click="uploadsStore.retryAllFailed()"
				>
					<Renew16 />
					<span>Retry all failed ({{ uploadsStore.retryableItems.length }})</span>
				</v-btn>
			</div>
		</div>
	</transition>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import CloudUpload16 from "@carbon/icons-vue/es/cloud--upload/16";
import Checkmark16 from "@carbon/icons-vue/es/checkmark/16";
import ErrorFilled16 from "@carbon/icons-vue/es/error--filled/16";
import Close16 from "@carbon/icons-vue/es/close/16";
import ChevronUp16 from "@carbon/icons-vue/es/chevron--up/16";
import ChevronDown16 from "@carbon/icons-vue/es/chevron--down/16";
import Renew16 from "@carbon/icons-vue/es/renew/16";
import { useUploadsStore } from "@/stores/uploads";

export default defineComponent({
	name: "UploadTray",
	components: { CloudUpload16, Checkmark16, ErrorFilled16, Close16, ChevronUp16, ChevronDown16, Renew16 },
	setup() {
		const uploadsStore = useUploadsStore();
		return { uploadsStore };
	},
	data: () => ({
		collapsed: false,
	}),
	computed: {
		headerText(): string {
			const total = this.uploadsStore.totalCount;
			if (this.uploadsStore.hasActive) {
				const current = Math.min(this.uploadsStore.settledCount + 1, total);
				return `Uploading ${current} of ${total}`;
			}
			if (this.uploadsStore.failedItems.length > 0) {
				return `${this.uploadsStore.successCount} of ${total} uploaded`;
			}
			return total === 1 ? "1 file uploaded" : `${total} files uploaded`;
		},
	},
	methods: {
		// Uploads live only in memory and the SDK cannot resume them, so a reload or tab
		// close would silently drop in-flight files.
		warnIfUploadsActive(event: BeforeUnloadEvent) {
			// A confirmed sign-out (App.vue) sets bypassUnloadGuard to avoid stacking a prompt on its dialog.
			if (this.uploadsStore.hasActive && !this.uploadsStore.bypassUnloadGuard) {
				event.preventDefault();
				event.returnValue = "";
			}
		},
	},
	mounted() {
		window.addEventListener("beforeunload", this.warnIfUploadsActive);
	},
	beforeUnmount() {
		window.removeEventListener("beforeunload", this.warnIfUploadsActive);
	},
});
</script>

<style lang="scss" scoped>
.upload-tray {
	position: fixed;
	right: var(--sgnl-space-24);
	bottom: var(--sgnl-space-24);
	z-index: 2000;
	width: 360px;
	max-width: calc(100vw - 2 * var(--sgnl-space-24));
	background-color: var(--sgnl-default-primary-background);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	border-radius: var(--sgnl-border-radius-8);
	box-shadow: 0 8px 24px rgb(0 0 0 / 12%);
	overflow: hidden;
}

.upload-tray__header {
	display: flex;
	align-items: center;
	gap: var(--sgnl-space-8);
	padding: var(--sgnl-space-12) var(--sgnl-space-16);
	border-bottom: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
}

.upload-tray__header-icon {
	color: var(--sgnl-default-secondary-icon);
	flex-shrink: 0;
}

.upload-tray__body {
	max-height: 280px;
	overflow-y: auto;
}

.upload-tray__row {
	display: flex;
	align-items: center;
	gap: var(--sgnl-space-12);
	padding: var(--sgnl-space-8) var(--sgnl-space-16);
}

.upload-tray__row + .upload-tray__row {
	border-top: var(--sgnl-border-width-1) solid var(--sgnl-default-secondary-border);
}

.upload-tray__status {
	display: flex;
	align-items: center;
	justify-content: center;
	width: var(--sgnl-icon-16);
	flex-shrink: 0;
}

.upload-tray__queued-dot {
	width: var(--sgnl-space-8);
	height: var(--sgnl-space-8);
	border-radius: 50%;
	background-color: var(--sgnl-default-tertiary-icon);
}

.upload-tray__details {
	min-width: 0;
	flex: 1;
}

.upload-tray__name {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.upload-tray__retry {
	flex-shrink: 0;
}

.upload-tray__remove {
	flex-shrink: 0;
	color: var(--sgnl-default-secondary-icon);
}

.upload-tray__footer {
	display: flex;
	justify-content: flex-end;
	padding: var(--sgnl-space-8) var(--sgnl-space-16);
	border-top: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
}

.upload-tray-fade-enter-active,
.upload-tray-fade-leave-active {
	transition:
		opacity var(--sgnl-motion-duration-moderate-01) var(--sgnl-expressive-effect-standard),
		transform var(--sgnl-motion-duration-moderate-01) var(--sgnl-expressive-effect-standard);
}

.upload-tray-fade-enter-from,
.upload-tray-fade-leave-to {
	opacity: 0;
	transform: translateY(var(--sgnl-space-8));
}

@media (max-width: 599px) {
	.upload-tray {
		right: var(--sgnl-space-12);
		left: var(--sgnl-space-12);
		bottom: var(--sgnl-space-12);
		width: auto;
	}
}
</style>
